// ─── src/services/vendorAuthService.js ───────────────────────────────────────
// Vendor authentication service - handles login, logout, activation, session
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from "../lib/supabaseClient";

/**
 * Login vendor with email and password
 * @param {string} email - Vendor email
 * @param {string} password - Vendor password
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const loginVendor = async (email, password) => {
  try {
    if (!supabase) {
      return { data: null, error: new Error("Supabase not configured") };
    }

    // Authenticate with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error("Login failed - no user returned");

    // Fetch vendor record
    const { data: vendorData, error: vendorError } = await supabase
      .from("vendors")
      .select("*")
      .eq("user_id", authData.user.id)
      .maybeSingle();

    if (vendorError) throw vendorError;
    if (!vendorData) throw new Error("Vendor profile not found");

    return { data: vendorData, error: null };
  } catch (error) {
    console.error("Error logging in vendor:", error);
    return { data: null, error };
  }
};

/**
 * Sign up new vendor with email and password
 * @param {string} email - Vendor email
 * @param {string} password - Vendor password
 * @param {string} vendorName - Vendor/venue name
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const signupVendor = async (email, password, vendorName) => {
  try {
    if (!supabase) {
      return { data: null, error: new Error("Supabase not configured") };
    }

    // Sign up with Supabase Auth
    // redirectTo points to confirmation page which handles email verification token
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        redirectTo: `${window.location.origin}/vendor/confirm-email`,
      },
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error("Signup failed - no user returned");

    // Create vendor record
    const { data: vendorData, error: vendorError } = await supabase
      .from("vendors")
      .insert([
        {
          user_id: authData.user.id,
          email: email,
          legacy_vendor_id: `vdr-${Date.now()}`,
          is_activated: true,
          name: vendorName,
        },
      ])
      .select()
      .single();

    if (vendorError) throw vendorError;
    if (!vendorData) throw new Error("Vendor record creation failed");

    return { data: vendorData, error: null };
  } catch (error) {
    console.error("Error signing up vendor:", error);
    return { data: null, error };
  }
};

/**
 * Logout vendor and clear session
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const logoutVendor = async () => {
  try {
    if (!supabase) {
      return { data: null, error: new Error("Supabase not configured") };
    }

    const { error } = await supabase.auth.signOut();

    if (error) throw error;

    return { data: { success: true }, error: null };
  } catch (error) {
    console.error("Error logging out vendor:", error);
    return { data: null, error };
  }
};

/**
 * Get currently authenticated vendor
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const getCurrentVendor = async () => {
  // Retry logic for handling lock errors during concurrent operations
  const maxRetries = 3;
  let lastError = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      if (!supabase) {
        return { data: null, error: null };
      }

      // Get current auth user
      const { data: authData, error: authError } = await supabase.auth.getUser();

      if (authError || !authData.user) {
        return { data: null, error: null };
      }

      // Fetch vendor record for this user
      const { data: vendorData, error: vendorError } = await supabase
        .from("vendors")
        .select("*")
        .eq("user_id", authData.user.id)
        .maybeSingle();

      if (vendorError) {
        // Check if it's a lock error - retry if so
        if (vendorError.message && vendorError.message.includes('Lock')) {
          lastError = vendorError;
          if (attempt < maxRetries - 1) {
            // Exponential backoff: 50ms, 100ms, 150ms
            await new Promise(resolve => setTimeout(resolve, 50 * (attempt + 1)));
            continue;
          }
        }
        // Vendor record doesn't exist, but user is authenticated
        return { data: null, error: null };
      }

      return { data: vendorData, error: null };
    } catch (error) {
      lastError = error;
      // If lock error, retry with backoff
      if (error.message && error.message.includes('Lock') && attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, 50 * (attempt + 1)));
        continue;
      }
      console.error("Error getting current vendor:", error);
      return { data: null, error: null };
    }
  }

  // After all retries failed due to lock, return null error
  return { data: null, error: null };
};

/**
 * Activate vendor account with invitation token
 * @param {string} activationToken - Token from invitation link
 * @param {string} password - Password to set for vendor
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const activateVendor = async (activationToken, password) => {
  try {
    if (!supabase) {
      return { data: null, error: new Error("Supabase not configured") };
    }

    // Activation runs entirely server-side in the `activate-vendor` edge function
    // (service_role): it validates the token, sets the auth user's password via the
    // admin API, marks the vendor activated, and burns the token. This keeps the
    // public.vendors table fully locked (no anon read/write) and fixes the old
    // path, which called auth.updateUser with no session.
    const { data, error } = await supabase.functions.invoke("activate-vendor", {
      body: { token: activationToken, password },
    });

    // functions.invoke surfaces a non-2xx as `error`, but the function also returns
    // a structured { error } body, surface the human-readable message from either.
    if (error) {
      let message = "Activation failed";
      try {
        const ctx = await error.context?.json?.();
        if (ctx?.error) message = ctx.error;
      } catch { /* fall through to default message */ }
      return { data: null, error: new Error(message) };
    }
    if (data?.error) {
      return { data: null, error: new Error(data.error) };
    }

    return {
      data: {
        success: true,
        message: data?.message || "Account activated successfully. You can now log in.",
      },
      error: null,
    };
  } catch (error) {
    console.error("Error activating vendor:", error);
    return { data: null, error };
  }
};

/**
 * Get auth user session (for checking if user is authenticated)
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const getSession = async () => {
  try {
    if (!supabase) {
      return { data: null, error: null };
    }

    const { data, error } = await supabase.auth.getSession();

    if (error) throw error;

    return { data: data?.session || null, error: null };
  } catch (error) {
    console.error("Error getting session:", error);
    return { data: null, error: null };
  }
};

/**
 * Listen for auth state changes
 * @param {Function} callback - Function to call when auth state changes
 * @returns {Function} Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  if (!supabase) return () => {};

  // supabase-js fires this callback WHILE it holds the auth lock (notably on the
  // INITIAL_SESSION event at load). Calling another auth method here, getCurrentVendor
  // awaits supabase.auth.getUser(), makes that call wait on the very lock this callback
  // holds, so the callback can never return: a deadlock that surfaces as "auth lock
  // timeout" and jams every authed query on the page. The fix (per Supabase's documented
  // onAuthStateChange rule) is to defer all follow-up work outside the callback with
  // setTimeout(…, 0): the callback returns synchronously, the lock releases, and only
  // then does getCurrentVendor acquire it cleanly. getCurrentVendor already fails safe and
  // retries internally, so no outer retry loop is needed.
  const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
    setTimeout(async () => {
      if (session?.user) {
        let vendor = null;
        try {
          const result = await getCurrentVendor();
          if (!result.error) vendor = result.data;
        } catch { /* getCurrentVendor is fail-safe; treat as no vendor record */ }
        callback({ user: session.user, vendor, isAuthenticated: true });
      } else {
        callback({ user: null, vendor: null, isAuthenticated: false });
      }
    }, 0);
  });

  // Return unsubscribe function
  return () => {
    authListener?.subscription?.unsubscribe();
  };
};

/**
 * Request password reset for vendor
 * Sends reset link to vendor email
 * @param {string} email - Vendor email
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const resetPasswordForEmail = async (email) => {
  try {
    if (!supabase) {
      return { data: null, error: new Error("Supabase not configured") };
    }

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/vendor/reset-password`,
    });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error("Error requesting password reset:", error);
    return { data: null, error };
  }
};

/**
 * Update password with reset token
 * Called after user clicks reset link in email
 * @param {string} newPassword - New password to set
 * @returns {Promise<{data: Object, error: null|Object}>}
 */
export const resetPassword = async (newPassword) => {
  try {
    if (!supabase) {
      return { data: null, error: new Error("Supabase not configured") };
    }

    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error("Error resetting password:", error);
    return { data: null, error };
  }
};
